import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Clock, Quote, Sparkles } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import { MarketingFooter } from "@/components/layout/MarketingFooter";

import { recruitBlogPosts } from "./data";

export const BlogPostPage = () => {
  const { slug } = useParams();
  const post = recruitBlogPosts.find((entry) => entry.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen" style={{ background: "#faf9f4", color: "#1b1c19" }}>
      <section className="relative overflow-hidden pt-28 pb-12">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 18% 18%, rgba(212,168,83,0.16) 0, transparent 32%), radial-gradient(circle at 86% 10%, rgba(123,88,4,0.08) 0, transparent 28%)",
          }}
        />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em]" style={{ color: "#7b5804" }}>
            <ArrowLeft className="h-4 w-4" />
            Back to journal
          </Link>

          <div className="mt-8 grid gap-10 lg:grid-cols-5">
            <div className="space-y-5 lg:col-span-3">
              <span
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.22em]"
                style={{
                  background: "rgba(212,168,83,0.12)",
                  border: "1px solid rgba(212,168,83,0.28)",
                  color: "#7b5804",
                }}
              >
                <Sparkles className="h-4 w-4" />
                {post.tags.join(" / ")}
              </span>
              <h1 className="font-display text-4xl md:text-6xl leading-[1.0] tracking-tight">
                {post.title}
              </h1>
              <p className="text-lg leading-relaxed" style={{ color: "#4e4637" }}>
                {post.description}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm" style={{ color: "#4e4637" }}>
                <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5" style={{ borderColor: "rgba(27,28,25,0.08)" }}>
                  <BookOpen className="h-4 w-4" style={{ color: "#7b5804" }} />
                  {post.readTime}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5" style={{ borderColor: "rgba(27,28,25,0.08)" }}>
                  <Clock className="h-4 w-4" style={{ color: "#7b5804" }} />
                  {post.publishedOn}
                </span>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="rounded-[28px] border p-6" style={{ background: "#1b1c19", borderColor: "rgba(212,168,83,0.12)" }}>
                <p className="text-[10px] font-bold uppercase tracking-[0.26em]" style={{ color: "rgba(212,168,83,0.70)" }}>
                  Why this matters
                </p>
                <p className="mt-4 text-lg leading-relaxed text-[rgba(250,249,244,0.80)]">
                  Recruiting systems need to surface evidence, not just produce rankings. This is
                  where product design and hiring judgment have to work together.
                </p>
                {post.pullQuote ? (
                  <div className="mt-6 rounded-2xl border p-4" style={{ borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)" }}>
                    <div className="flex items-start gap-3">
                      <Quote className="mt-0.5 h-5 w-5" style={{ color: "#d4a853" }} />
                      <p className="font-display italic text-lg leading-relaxed text-[rgba(250,249,244,0.92)]">
                        {post.pullQuote}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div
            className="mx-auto max-w-5xl rounded-[32px] border p-7 md:p-10"
            style={{
              background: "rgba(255,255,255,0.90)",
              borderColor: "rgba(27,28,25,0.08)",
              boxShadow: "0 24px 60px rgba(27,28,25,0.08)",
            }}
          >
            {post.sections.map((section, index) => (
              <article key={section.heading ?? index} className={index === 0 ? "" : "mt-10"}>
                {section.heading ? (
                  <motion.h2
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="font-display text-3xl leading-tight tracking-tight"
                  >
                    {section.heading}
                  </motion.h2>
                ) : null}
                <div className="space-y-4 mt-4">
                  {section.body.map((paragraph) => (
                    <motion.p
                      key={paragraph}
                      initial={{ opacity: 0, y: 6 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="text-lg leading-relaxed"
                      style={{ color: "#4e4637" }}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
                {section.bullets ? (
                  <ul className="mt-5 space-y-3">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3 text-lg leading-relaxed" style={{ color: "#4e4637" }}>
                        <span className="mt-3 h-2 w-2 rounded-full" style={{ background: "#d4a853" }} />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default BlogPostPage;
